// Elite x2 テイクアウト注文管理画面
import React, { useState, useEffect } from 'react';
import apiClient, { getApiUrl } from '../../utils/apiClient';
import { Search, Package, Clock, CheckCircle, AlertCircle, Printer, RefreshCw } from 'lucide-react';import PageContainer from '../layout/PageContainer';




interface OrderItem {
  id: string;
  name: string;
  price: number;
  quantity: number;
}

interface TakeoutOrder {
  id: number;
  order_number: string;
  customer_name: string;
  customer_phone: string;
  orderItems: OrderItem[];
  total_amount: number;
  pickup_time: string;
  payment_method: string;
  payment_status: string;
  order_status: string;
  special_requests?: string;
  created_at: string;
  picked_up_at?: string;
}

interface ProductionSummary {
  [key: string]: {
    name: string;
    quantity: number;
    orders: {
      orderNumber: string;
      customerName: string;
      quantity: number;
    }[];
  };
}

const TakeoutManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'orders' | 'production'>('orders');
  const [orders, setOrders] = useState<TakeoutOrder[]>([]);
  const [productionSummary, setProductionSummary] = useState<ProductionSummary>({});
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedOrder, setSelectedOrder] = useState<TakeoutOrder | null>(null);
  const [loading, setLoading] = useState(false);
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());

  useEffect(() => {
    loadTodayOrders();
    loadProductionSummary();
    
    // 自動更新（30秒ごと）
    const interval = setInterval(() => {
      loadTodayOrders();
      loadProductionSummary();
    }, 30000);
    
    return () => clearInterval(interval);
  }, []);

  const loadTodayOrders = async () => {
    try {
      const response = await fetch(`${getApiUrl()}/takeout/orders/today`);
      const data = await response.json();
      if (data.success) {
        setOrders(data.orders);
        setLastUpdate(new Date());
      }
    } catch (error) {
      console.error('注文一覧取得エラー:', error);
    }
  };

  const loadProductionSummary = async () => {
    try {
      const response = await fetch(`${getApiUrl()}/takeout/production/summary`);
      const data = await response.json();
      if (data.success) {
        setProductionSummary(data.summary);
      }
    } catch (error) {
      console.error('製造数集計取得エラー:', error);
    }
  };

  const searchOrder = async () => {
    if (!searchQuery) {
      loadTodayOrders();
      return;
    }
    
    try {
      const response = await fetch(`${getApiUrl()}/takeout/search?q=${searchQuery}`);
      const data = await response.json();
      if (data.success) {
        setOrders(data.orders);
      }
    } catch (error) {
      console.error('注文検索エラー:', error);
    }
  };

  const updateOrderStatus = async (orderNumber: string, status: string) => {
    try {
      const response = await fetch(`${getApiUrl()}/takeout/order/${orderNumber}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status })
      });
      
      if (response.ok) {
        loadTodayOrders();
        setSelectedOrder(null);
      }
    } catch (error) {
      console.error('ステータス更新エラー:', error);
    }
  };

  const markAsReady = async (orderNumber: string) => {
    try {
      const response = await fetch(`${getApiUrl()}/takeout/order/${orderNumber}/ready`, {
        method: 'POST'
      });
      
      if (response.ok) {
        loadTodayOrders();
      }
    } catch (error) {
      console.error('製造完了処理エラー:', error);
    }
  };

  const closeMorningOrders = async () => {
    if (!confirm('9時締切処理を実行しますか？')) return;
    
    try {
      setLoading(true);
      const response = await fetch(`${getApiUrl()}/takeout/production/close`, {
        method: 'POST'
      });
      
      if (response.ok) {
        alert('締切処理が完了しました');
        loadTodayOrders();
        loadProductionSummary();
      }
    } catch (error) {
      console.error('締切処理エラー:', error);
      alert('締切処理に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-100 text-yellow-800', label: '受付中' },
      production: { color: 'bg-blue-100 text-blue-800', label: '製造中' },
      ready: { color: 'bg-green-100 text-green-800', label: '完成' },
      completed: { color: 'bg-gray-100 text-gray-800', label: '受渡済' },
      cancelled: { color: 'bg-red-100 text-red-800', label: 'キャンセル' }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const getPaymentBadge = (method: string, status: string) => {
    if (method === 'cash') {
      return <span className="text-xs text-gray-600">現金（店頭）</span>;
    }
    return (
      <span className={`text-xs ${status === 'completed' ? 'text-green-600' : 'text-orange-600'}`}>
        {method === 'paypay' ? 'PayPay' : 'クレジット'}
        {status === 'completed' ? '（決済済）' : '（未決済）'}
      </span>
    );
  };

  return (
    <div className="flex h-screen bg-gray-100">
      {/* サイドバー */}
      <div className="w-80 bg-white shadow-lg overflow-y-auto">
        <div className="p-4 bg-orange-600 text-white">
          <h2 className="text-xl font-bold">🍱 テイクアウト管理</h2>
          <p className="text-sm mt-1">最終更新: {lastUpdate.toLocaleTimeString()}</p>
        </div>
        
        {/* タブ切り替え */}
        <div className="flex border-b">
          <button
            onClick={() => setActiveTab('orders')}
            className={`flex-1 py-3 ${activeTab === 'orders' ? 'bg-orange-50 border-b-2 border-orange-500' : ''}`}
          >
            注文一覧
          </button>
          <button
            onClick={() => setActiveTab('production')}
            className={`flex-1 py-3 ${activeTab === 'production' ? 'bg-orange-50 border-b-2 border-orange-500' : ''}`}
          >
            製造管理
          </button>
        </div>
        
        {activeTab === 'orders' ? (
          <>
            {/* 検索バー */}
            <div className="p-4 border-b">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && searchOrder()}
                  placeholder="注文番号・名前・電話番号"
                  className="flex-1 px-3 py-2 border rounded-lg"
                />
                <button
                  onClick={searchOrder}
                  className="p-2 bg-orange-500 text-white rounded-lg"
                >
                  <Search size={20} />
                </button>
              </div>
            </div>
            
            {/* 注文リスト */}
            <div className="p-2">
              {orders.map((order) => (
                <div
                  key={order.id}
                  onClick={() => setSelectedOrder(order)}
                  className={`p-3 mb-2 rounded-lg cursor-pointer transition ${
                    selectedOrder?.id === order.id
                      ? 'bg-orange-100 border border-orange-300'
                      : 'bg-gray-50 hover:bg-gray-100'
                  }`}
                >
                  <div className="flex justify-between items-start mb-2">
                    <span className="font-bold text-sm">{order.order_number}</span>
                    {getStatusBadge(order.order_status)}
                  </div>
                  <div className="text-sm">
                    <div>{order.customer_name}</div>
                    <div className="text-gray-600">{order.customer_phone}</div>
                    <div className="font-semibold mt-1">
                      ¥{order.total_amount.toLocaleString()}
                    </div>
                    {getPaymentBadge(order.payment_method, order.payment_status)}
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          /* 製造管理タブ */
          <div className="p-4">
            <button
              onClick={closeMorningOrders}
              disabled={loading}
              className="w-full mb-4 py-2 bg-red-500 text-white rounded-lg font-bold disabled:bg-gray-400"
            >
              {loading ? '処理中...' : '9時締切処理実行'}
            </button>
            
            <h3 className="font-bold mb-3">本日の製造必要数</h3>
            {Object.entries(productionSummary).map(([itemId, data]) => (
              <div key={itemId} className="mb-4 p-3 bg-gray-50 rounded-lg">
                <div className="flex justify-between items-center mb-2">
                  <span className="font-bold">{data.name}</span>
                  <span className="text-xl font-bold text-orange-600">
                    {data.quantity}個
                  </span>
                </div>
                <div className="text-xs text-gray-600">
                  {data.orders.map((order, idx) => (
                    <div key={idx}>
                      {order.orderNumber}: {order.customerName} ({order.quantity}個)
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* メインコンテンツ */}
      <div className="flex-1 overflow-y-auto">
        {selectedOrder ? (
          <div className="p-6">
            <div className="bg-white rounded-lg shadow-lg p-6">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h2 className="text-2xl font-bold mb-2">
                    注文詳細 #{selectedOrder.order_number}
                  </h2>
                  <div className="flex gap-4">
                    {getStatusBadge(selectedOrder.order_status)}
                    {getPaymentBadge(selectedOrder.payment_method, selectedOrder.payment_status)}
                  </div>
                </div>
                <button
                  onClick={loadTodayOrders}
                  className="p-2 bg-gray-200 rounded-lg"
                >
                  <RefreshCw size={20} />
                </button>
              </div>
              
              {/* 顧客情報 */}
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <h3 className="font-bold mb-2">顧客情報</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="text-sm text-gray-600">お名前</span>
                    <div className="font-semibold">{selectedOrder.customer_name}</div>
                  </div>
                  <div>
                    <span className="text-sm text-gray-600">電話番号</span>
                    <div className="font-semibold">{selectedOrder.customer_phone}</div>
                  </div>
                </div>
                {selectedOrder.special_requests && (
                  <div className="mt-4">
                    <span className="text-sm text-gray-600">特別要望</span>
                    <div className="p-2 bg-yellow-50 rounded mt-1">
                      {selectedOrder.special_requests}
                    </div>
                  </div>
                )}
              </div>
              
              {/* 注文内容 */}
              <div className="mb-6">
                <h3 className="font-bold mb-2">注文内容</h3>
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2">商品名</th>
                      <th className="text-right py-2">単価</th>
                      <th className="text-right py-2">数量</th>
                      <th className="text-right py-2">小計</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedOrder.orderItems.map((item, idx) => (
                      <tr key={idx} className="border-b">
                        <td className="py-2">{item.name}</td>
                        <td className="text-right">¥{item.price.toLocaleString()}</td>
                        <td className="text-right">{item.quantity}</td>
                        <td className="text-right font-semibold">
                          ¥{(item.price * item.quantity).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={3} className="text-right pt-4 font-bold">合計</td>
                      <td className="text-right pt-4 text-xl font-bold text-orange-600">
                        ¥{selectedOrder.total_amount.toLocaleString()}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
              
              {/* アクションボタン */}
              <div className="flex gap-3">
                {selectedOrder.order_status === 'pending' && (
                  <button
                    onClick={() => updateOrderStatus(selectedOrder.order_number, 'production')}
                    className="flex-1 py-3 bg-blue-500 text-white rounded-lg font-bold"
                  >
                    製造開始
                  </button>
                )}
                {selectedOrder.order_status === 'production' && (
                  <button
                    onClick={() => markAsReady(selectedOrder.order_number)}
                    className="flex-1 py-3 bg-green-500 text-white rounded-lg font-bold"
                  >
                    製造完了
                  </button>
                )}
                {selectedOrder.order_status === 'ready' && (
                  <button
                    onClick={() => updateOrderStatus(selectedOrder.order_number, 'completed')}
                    className="flex-1 py-3 bg-gray-700 text-white rounded-lg font-bold"
                  >
                    受渡完了
                  </button>
                )}
                <button
                  onClick={() => window.print()}
                  className="px-6 py-3 bg-gray-200 rounded-lg font-bold"
                >
                  <Printer className="inline mr-2" size={20} />
                  印刷
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center h-full text-gray-400">
            <div className="text-center">
              <Package size={64} className="mx-auto mb-4" />
              <p>注文を選択してください</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TakeoutManagement;